import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';
import { AnalyzerConfig, Finding } from '../types/contractAnalyzer';

const API_BASE_URL = `${ENV.API_URL}/api`;

async function getToken(): Promise<string> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('Error getting Supabase auth token:', e);
    return '';
  }
}

export interface SaveRecommendationsPayload {
  config: AnalyzerConfig;
  findings: Finding[];
}

export async function saveRecommendations(payload: SaveRecommendationsPayload): Promise<{ id: string } | { message: string }> {
  // Validate input before sending
  if (!payload || !payload.config || !Array.isArray(payload.findings)) {
    throw new Error('Invalid payload for saving recommendations');
  }
  if (payload.findings.length === 0) {
    throw new Error('No findings to save');
  }

  const url = `${API_BASE_URL}/contract-analyzer/sessions`;
  try {
    const token = await getToken();
    if (!token) {
      throw new Error('Authentication required to save recommendations');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        config: payload.config,
        // Persist findings metadata only. Do NOT include full contract text.
        findings: payload.findings.map(f => ({
          id: f.id,
          category: f.category,
          severity: f.severity,
          actionType: f.actionType,
          title: f.title,
          summary: f.summary,
          recommendation: f.recommendation,
          jurisdiction: f.jurisdiction,
          contractType: f.contractType,
          confidence: f.confidence,
          createdAt: f.createdAt,
          // redact long clause text; store short excerpt for context only
          clauseSpan: {
            ...f.clauseSpan,
            text: (f.clauseSpan?.text || '').slice(0, 240)
          }
        }))
      })
    });

    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Save failed: ${response.status} ${response.statusText}. ${message}`);
    }

    const data = await response.json();
    if (!data || (!data.id && !data.message)) {
      throw new Error('Unexpected response from server while saving recommendations');
    }
    return data;
  } catch (err) {
    console.error('Error saving recommendations:', err);
    throw err instanceof Error ? err : new Error('Unknown error saving recommendations');
  }
}
